/**
 * TaskEditPanel Component
 *
 * Main panel showing category cards for task editing.
 * Each category card shows a preview and expands to show the edit view.
 *
 * @param {Object} props
 * @param {Array} props.categories - Array of category objects with configItems
 * @param {Function} props.onCategoryEdit - Callback when a category is selected for editing
 * @param {Object} props.selectedCategory - Currently selected category
 */

import React from "react";

const CategoryCard = ({ category, onCategoryEdit, isSelected = false }) => (
  <button
    onClick={() => onCategoryEdit(category)}
    className={`w-full text-left px-3 py-2.5 rounded-lg transition-colors
               ${isSelected
                 ? "bg-gray-100 dark:bg-gray-800/60"
                 : "hover:bg-gray-50 dark:hover:bg-gray-800/40"
               }`}
  >
    <div className="flex items-center gap-3">
      {/* Colored icon with background */}
      <div className={`p-1.5 rounded-lg ${category.colorClasses?.bg || "bg-gray-100 dark:bg-gray-800"} ${category.colorClasses?.border ? `border ${category.colorClasses.border}` : ""}`}>
        {category.Icon && (
          <category.Icon className={`w-4 h-4 ${category.colorClasses?.text || "text-gray-500"}`} />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-gray-800 dark:text-gray-200">
          {category.label}
        </div>
        <div className="text-xs text-gray-400 dark:text-gray-500 truncate">
          {category.preview}
        </div>
      </div>
    </div>
  </button>
);

const TaskEditPanel = ({ categories, onCategoryEdit, selectedCategory = null }) => {
  // Sort categories: general first, then core, then others
  const sortedCategories = [...categories].sort((a, b) => {
    if (a.id === "general") return -1;
    if (b.id === "general") return 1;
    if (a.id === "core") return -1;
    if (b.id === "core") return 1;
    return 0;
  });

  return (
    <div className="h-full flex flex-col">
      {/* Category list */}
      <div className="flex-1 overflow-y-auto p-2">
        <div className="space-y-0.5">
          {/* General & Automation categories */}
          {sortedCategories
            .filter((c) => c.id === "general" || c.id === "core")
            .map((category) => (
              <CategoryCard
                key={category.id}
                category={category}
                onCategoryEdit={onCategoryEdit}
                isSelected={selectedCategory?.id === category.id}
              />
            ))}

          {/* Configuration section */}
          {sortedCategories.filter((c) => c.id !== "general" && c.id !== "core").length > 0 && (
            <>
              <div className="pt-3 pb-1 px-3">
                <span className="text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider">
                  Configuration
                </span>
              </div>
              {sortedCategories
                .filter((c) => c.id !== "general" && c.id !== "core")
                .map((category) => (
                  <CategoryCard
                    key={category.id}
                    category={category}
                    onCategoryEdit={onCategoryEdit}
                    isSelected={selectedCategory?.id === category.id}
                  />
                ))}
            </>
          )}
        </div>

        {categories.length === 0 && (
          <div className="flex items-center justify-center h-32 text-gray-400 dark:text-gray-500 text-sm">
            No options available
          </div>
        )}
      </div>
    </div>
  );
};

export default TaskEditPanel;
